"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import type { ReactNode } from "react";
import type { LaptopInformation, LaptopScores, PricePoint, Seller, UserReview, WikiCitation } from "@/lib/types";
import { ScoreDetailModal } from "@/components/score-detail-modal";
import { ReportActions } from "@/components/report-actions";
import { useBuyerSurveyGate, type BuyClickHandler } from "@/components/buyer-survey-modal";
import { AffiliateDisclosure } from "@/components/affiliate-disclosure";
import { stripCitations, toSlug } from "@/lib/utils";
import { displayPrice, getAmazonTarget, normCurrencySymbol, sellerUrl } from "@/lib/amazon";
import { PriceHistorySection } from "@/components/price-history-section";
import { WishlistButton } from "@/components/wishlist-button";
import { PriceSparkline } from "@/components/price-sparkline";
import { usePriceHistory } from "@/hooks/use-price-history";
import { conditionBadge, conditionRank, conditionTooltip, isRefurbished } from "@/lib/condition";
import { type Answer, type Reference, capitalize, scoreBand, scoreColor } from "@/lib/match-explanations";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
function barWidth(score: number): string {
  return `${Math.max(0, Math.min(100, score))}%`;
}
function IconZap() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 16 16" fill="currentColor" className="size-3 shrink-0 text-muted-foreground"><path d="M9.5 1.5 3 9h5.5L6.5 14.5 13 7H7.5L9.5 1.5Z" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function IconDisplay() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 16 16" fill="none" className="size-3 shrink-0"><rect x="1" y="2" width="14" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.25" /><path d="M5 13h6M8 11v2" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function IconCpu() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 16 16" fill="none" className="size-3 shrink-0"><rect x="4" y="4" width="8" height="8" rx="1" stroke="currentColor" strokeWidth="1.25" /><path d="M6 2v2M10 2v2M6 12v2M10 12v2M2 6h2M2 10h2M12 6h2M12 10h2" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function IconLeaf() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 16 16" fill="none" className="size-3 shrink-0"><path d="M2.5 13.5C3.5 8.5 6 5 13.5 2.5 11 9.5 8.5 12 2.5 13.5Z" stroke="currentColor" strokeWidth="1.25" strokeLinejoin="round" /><path d="M2.5 13.5L6 10" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function IconCheck() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 16 16" fill="currentColor" className="size-2.5 text-brand"><path fillRule="evenodd" d="M13.78 4.22a.75.75 0 0 1 0 1.06l-7 7a.75.75 0 0 1-1.06 0l-3.5-3.5a.75.75 0 1 1 1.06-1.06L6.25 10.69l6.47-6.47a.75.75 0 0 1 1.06 0Z" clipRule="evenodd" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function IconCross() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 16 16" fill="currentColor" className="size-2.5"><path d="M3.72 3.72a.75.75 0 0 1 1.06 0L8 6.94l3.22-3.22a.75.75 0 1 1 1.06 1.06L9.06 8l3.22 3.22a.75.75 0 1 1-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 0 1-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 0 1 0-1.06Z" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function BoolBadge(t0) {
  const $ = _c(6);
  const {
    value
  } = t0;
  const t1 = `inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${value ? "bg-brand/10 text-brand ring-1 ring-brand/20" : "bg-foreground/5 text-muted-foreground ring-1 ring-border"}`;
  let t2;
  if ($[0] !== value) {
    t2 = value ? <IconCheck /> : <IconCross />;
    $[0] = value;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = value ? "Yes" : "No";
  let t4;
  if ($[2] !== t1 || $[3] !== t2 || $[4] !== t3) {
    t4 = <span className={t1}>{t2}{t3}</span>;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
function StarRating(t0) {
  const $ = _c(9);
  const {
    rating
  } = t0;
  const full = Math.round(rating);
  let t1;
  if ($[0] !== full) {
    t1 = "\u2605".repeat(Math.max(0, full));
    $[0] = full;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== full) {
    t2 = "\u2605".repeat(Math.max(0, 5 - full));
    $[2] = full;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <span className="text-border">{t2}</span>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t3) {
    t4 = <span className="text-[11px] text-amber-400">{t1}{t3}</span>;
    $[6] = t1;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function SellerCard(t0) {
  const $ = _c(132);
  const {
    seller,
    cheapest: t1,
    country,
    onBuyClick,
    history
  } = t0;
  const cheapest = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== seller.currency) {
    t2 = normCurrencySymbol(seller.currency);
    $[0] = seller.currency;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const sym = t2;
  let t3;
  if ($[2] !== country || $[3] !== seller) {
    t3 = sellerUrl(seller, country);
    $[2] = country;
    $[3] = seller;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const url = t3;
  const freeDelivery = seller.delivery_price === 0;
  const displayName = seller.seller_name || seller.vendor_name || "Seller";
  let t4;
  if ($[5] !== seller) {
    t4 = isRefurbished(seller);
    $[5] = seller;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const refurbished = t4;
  let t5;
  if ($[7] !== seller) {
    t5 = conditionBadge(seller);
    $[7] = seller;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const badge = t5;
  let t6;
  if ($[9] !== seller) {
    t6 = conditionTooltip(seller);
    $[9] = seller;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const tooltip = t6;
  let t10;
  let t11;
  let t12;
  let t7;
  let t8;
  let t9;
  if ($[11] !== badge || $[12] !== cheapest || $[13] !== displayName || $[14] !== freeDelivery || $[15] !== history || $[16] !== refurbished || $[17] !== seller.condition_description || $[18] !== seller.delivery_price || $[19] !== seller.display_inches || $[20] !== seller.display_size_match || $[21] !== seller.graphics || $[22] !== seller.graphics_match || $[23] !== seller.images || $[24] !== seller.item_price || $[25] !== seller.memory_size || $[26] !== seller.operating_system || $[27] !== seller.operating_system_match || $[28] !== seller.processor || $[29] !== seller.processor_match || $[30] !== seller.ram_size_match || $[31] !== seller.rating || $[32] !== seller.reviews_count || $[33] !== seller.seller_name || $[34] !== seller.storage_size_in_gigabtyes || $[35] !== seller.storage_size_match || $[36] !== seller.storage_type || $[37] !== seller.title || $[38] !== seller.vendor_name || $[39] !== sym || $[40] !== tooltip) {
    const photos = refurbished ? (seller.images ?? []).slice(0, 4) : [];
    let t13;
    if ($[47] !== seller.processor || $[48] !== seller.processor_match) {
      t13 = {
        label: "Processor",
        value: seller.processor,
        match: seller.processor_match
      };
      $[47] = seller.processor;
      $[48] = seller.processor_match;
      $[49] = t13;
    } else {
      t13 = $[49];
    }
    let t14;
    if ($[50] !== seller.graphics || $[51] !== seller.graphics_match) {
      t14 = {
        label: "Graphics",
        value: seller.graphics,
        match: seller.graphics_match
      };
      $[50] = seller.graphics;
      $[51] = seller.graphics_match;
      $[52] = t14;
    } else {
      t14 = $[52];
    }
    const t15 = seller.memory_size != null ? `${seller.memory_size} GB` : null;
    let t16;
    if ($[53] !== seller.ram_size_match || $[54] !== t15) {
      t16 = {
        label: "RAM",
        value: t15,
        match: seller.ram_size_match
      };
      $[53] = seller.ram_size_match;
      $[54] = t15;
      $[55] = t16;
    } else {
      t16 = $[55];
    }
    const t17 = seller.storage_size_in_gigabtyes != null ? `${seller.storage_size_in_gigabtyes} GB${seller.storage_type ? ` ${seller.storage_type}` : ""}` : null;
    let t18;
    if ($[56] !== seller.storage_size_match || $[57] !== t17) {
      t18 = {
        label: "Storage",
        value: t17,
        match: seller.storage_size_match
      };
      $[56] = seller.storage_size_match;
      $[57] = t17;
      $[58] = t18;
    } else {
      t18 = $[58];
    }
    const t19 = seller.display_inches != null ? `${seller.display_inches}"` : null;
    let t20;
    if ($[59] !== seller.display_size_match || $[60] !== t19) {
      t20 = {
        label: "Display",
        value: t19,
        match: seller.display_size_match
      };
      $[59] = seller.display_size_match;
      $[60] = t19;
      $[61] = t20;
    } else {
      t20 = $[61];
    }
    let t21;
    if ($[62] !== seller.operating_system || $[63] !== seller.operating_system_match) {
      t21 = {
        label: "OS",
        value: seller.operating_system,
        match: seller.operating_system_match
      };
      $[62] = seller.operating_system;
      $[63] = seller.operating_system_match;
      $[64] = t21;
    } else {
      t21 = $[64];
    }
    let t22;
    if ($[65] !== t13 || $[66] !== t14 || $[67] !== t16 || $[68] !== t18 || $[69] !== t20 || $[70] !== t21) {
      t22 = [t13, t14, t16, t18, t20, t21].filter(_temp);
      $[65] = t13;
      $[66] = t14;
      $[67] = t16;
      $[68] = t18;
      $[69] = t20;
      $[70] = t21;
      $[71] = t22;
    } else {
      t22 = $[71];
    }
    const specRows = t22;
    t7 = "rounded-2xl bg-surface p-4 ring-1 ring-border/60";
    let t23;
    if ($[72] !== displayName) {
      t23 = <p className="truncate text-sm font-semibold text-foreground">{displayName}</p>;
      $[72] = displayName;
      $[73] = t23;
    } else {
      t23 = $[73];
    }
    let t24;
    if ($[74] !== cheapest) {
      t24 = cheapest && <span className="shrink-0 rounded-full bg-brand/10 px-2 py-0.5 text-[10px] font-bold text-brand ring-1 ring-brand/20">Cheapest</span>;
      $[74] = cheapest;
      $[75] = t24;
    } else {
      t24 = $[75];
    }
    let t25;
    if ($[76] !== badge || $[77] !== tooltip) {
      t25 = badge && <span title={tooltip ?? undefined} className="shrink-0 rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold text-amber-600 ring-1 ring-amber-500/20 dark:text-amber-400">{badge}</span>;
      $[76] = badge;
      $[77] = tooltip;
      $[78] = t25;
    } else {
      t25 = $[78];
    }
    let t26;
    if ($[79] !== t23 || $[80] !== t24 || $[81] !== t25) {
      t26 = <div className="flex items-center gap-2">{t23}{t24}{t25}</div>;
      $[79] = t23;
      $[80] = t24;
      $[81] = t25;
      $[82] = t26;
    } else {
      t26 = $[82];
    }
    let t27;
    if ($[83] !== seller.seller_name || $[84] !== seller.vendor_name) {
      t27 = seller.vendor_name && seller.vendor_name !== seller.seller_name && <p className="mt-0.5 text-[11px] text-muted-foreground">{seller.vendor_name}</p>;
      $[83] = seller.seller_name;
      $[84] = seller.vendor_name;
      $[85] = t27;
    } else {
      t27 = $[85];
    }
    let t28;
    if ($[86] !== seller.rating || $[87] !== seller.reviews_count) {
      t28 = seller.rating > 0 && <div className="mt-1 flex items-center gap-1.5"><StarRating rating={seller.rating} />{seller.reviews_count > 0 && <span className="text-[11px] text-muted-foreground">({seller.reviews_count.toLocaleString()})</span>}</div>;
      $[86] = seller.rating;
      $[87] = seller.reviews_count;
      $[88] = t28;
    } else {
      t28 = $[88];
    }
    let t29;
    if ($[89] !== t26 || $[90] !== t27 || $[91] !== t28) {
      t29 = <div className="min-w-0">{t26}{t27}{t28}</div>;
      $[89] = t26;
      $[90] = t27;
      $[91] = t28;
      $[92] = t29;
    } else {
      t29 = $[92];
    }
    let t30;
    if ($[93] !== seller.item_price || $[94] !== sym) {
      t30 = seller.item_price != null ? `${sym}${Math.round(seller.item_price).toLocaleString()}` : "\u2014";
      $[93] = seller.item_price;
      $[94] = sym;
      $[95] = t30;
    } else {
      t30 = $[95];
    }
    let t31;
    if ($[96] !== t30) {
      t31 = <p className="text-xl font-bold tabular-nums leading-none">{t30}</p>;
      $[96] = t30;
      $[97] = t31;
    } else {
      t31 = $[97];
    }
    let t32;
    if ($[98] !== freeDelivery || $[99] !== seller.delivery_price || $[100] !== sym) {
      t32 = seller.delivery_price != null && <p className={`mt-1 text-[11px] font-medium ${freeDelivery ? "text-brand" : "text-muted-foreground"}`}>{freeDelivery ? "Free delivery" : `+${sym}${seller.delivery_price.toFixed(2)} delivery`}</p>;
      $[98] = freeDelivery;
      $[99] = seller.delivery_price;
      $[100] = sym;
      $[101] = t32;
    } else {
      t32 = $[101];
    }
    let t33;
    if ($[102] !== history) {
      t33 = history && history.length > 1 && <PriceSparkline points={history} className="mt-1.5 ml-auto" />;
      $[102] = history;
      $[103] = t33;
    } else {
      t33 = $[103];
    }
    let t34;
    if ($[104] !== t31 || $[105] !== t32 || $[106] !== t33) {
      t34 = <div className="shrink-0 text-right">{t31}{t32}{t33}</div>;
      $[104] = t31;
      $[105] = t32;
      $[106] = t33;
      $[107] = t34;
    } else {
      t34 = $[107];
    }
    if ($[108] !== t29 || $[109] !== t34) {
      t8 = <div className="flex items-start justify-between gap-4">{t29}{t34}</div>;
      $[108] = t29;
      $[109] = t34;
      $[110] = t8;
    } else {
      t8 = $[110];
    }
    if ($[111] !== seller.title) {
      t9 = seller.title && <p className="mt-2.5 line-clamp-2 text-[11px] leading-relaxed text-muted-foreground">{seller.title}</p>;
      $[111] = seller.title;
      $[112] = t9;
    } else {
      t9 = $[112];
    }
    if ($[113] !== refurbished || $[114] !== seller.condition_description) {
      t10 = refurbished && seller.condition_description && <p className="mt-2 rounded-lg bg-amber-500/5 px-2.5 py-1.5 text-[11px] leading-relaxed text-muted-foreground ring-1 ring-amber-500/15">{seller.condition_description}</p>;
      $[113] = refurbished;
      $[114] = seller.condition_description;
      $[115] = t10;
    } else {
      t10 = $[115];
    }
    t11 = photos.length > 0 && <div className="mt-2.5 flex gap-1.5 overflow-x-auto">{photos.map(src => <img key={src} src={src} alt={`${displayName} listing photo`} loading="lazy" className="size-14 shrink-0 rounded-lg bg-card object-contain ring-1 ring-border/60" />)}</div>;
    t12 = specRows.length > 0 && <dl className="mt-3 space-y-1 border-t border-border/60 pt-3">{specRows.map(_temp2)}</dl>;
    $[11] = badge;
    $[12] = cheapest;
    $[13] = displayName;
    $[14] = freeDelivery;
    $[15] = history;
    $[16] = refurbished;
    $[17] = seller.condition_description;
    $[18] = seller.delivery_price;
    $[19] = seller.display_inches;
    $[20] = seller.display_size_match;
    $[21] = seller.graphics;
    $[22] = seller.graphics_match;
    $[23] = seller.images;
    $[24] = seller.item_price;
    $[25] = seller.memory_size;
    $[26] = seller.operating_system;
    $[27] = seller.operating_system_match;
    $[28] = seller.processor;
    $[29] = seller.processor_match;
    $[30] = seller.ram_size_match;
    $[31] = seller.rating;
    $[32] = seller.reviews_count;
    $[33] = seller.seller_name;
    $[34] = seller.storage_size_in_gigabtyes;
    $[35] = seller.storage_size_match;
    $[36] = seller.storage_type;
    $[37] = seller.title;
    $[38] = seller.vendor_name;
    $[39] = sym;
    $[40] = tooltip;
    $[41] = t10;
    $[42] = t11;
    $[43] = t12;
    $[44] = t7;
    $[45] = t8;
    $[46] = t9;
  } else {
    t10 = $[41];
    t11 = $[42];
    t12 = $[43];
    t7 = $[44];
    t8 = $[45];
    t9 = $[46];
  }
  let t13;
  if ($[116] !== displayName || $[117] !== onBuyClick || $[118] !== url) {
    t13 = url && <a href={url} target="_blank" rel="noopener noreferrer" onClick={e => onBuyClick?.(e, {
      url,
      retailer: displayName
    })} className="mt-3 flex h-9 w-full items-center justify-center gap-1.5 rounded-xl border border-border text-[11px] font-semibold text-foreground transition-colors hover:border-foreground/40 hover:bg-card active:scale-[0.98]">Buy from {displayName}<svg viewBox="0 0 16 16" fill="none" className="size-3 shrink-0"><path d="M6.5 3.5H3a.5.5 0 0 0-.5.5v9a.5.5 0 0 0 .5.5h9a.5.5 0 0 0 .5-.5V9.5M9.5 2.5H13.5V6.5M13.5 2.5L7 9" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" /></svg></a>;
    $[116] = displayName;
    $[117] = onBuyClick;
    $[118] = url;
    $[119] = t13;
  } else {
    t13 = $[119];
  }
  let t14;
  if ($[120] !== displayName || $[121] !== url) {
    t14 = url && <AffiliateDisclosure retailer={displayName} className="mt-2" />;
    $[120] = displayName;
    $[121] = url;
    $[122] = t14;
  } else {
    t14 = $[122];
  }
  let t15;
  if ($[123] !== t10 || $[124] !== t11 || $[125] !== t12 || $[126] !== t13 || $[127] !== t14 || $[128] !== t7 || $[129] !== t8 || $[130] !== t9) {
    t15 = <div className={t7}>{t8}{t9}{t10}{t11}{t12}{t13}{t14}</div>;
    $[123] = t10;
    $[124] = t11;
    $[125] = t12;
    $[126] = t13;
    $[127] = t14;
    $[128] = t7;
    $[129] = t8;
    $[130] = t9;
    $[131] = t15;
  } else {
    t15 = $[131];
  }
  return t15;
}
function _temp2(r_0) {
  return <div key={r_0.label} className="flex items-start justify-between gap-3 text-[11px]"><dt className="shrink-0 text-muted-foreground">{r_0.label}</dt><dd className="flex min-w-0 items-center justify-end gap-1 text-right font-medium text-foreground"><span className="truncate">{r_0.value}</span>{r_0.match && <IconCheck />}</dd></div>;
}
function _temp(r) {
  return r.value;
}
function SellersDrawer(t0) {
  const $ = _c(45);
  const {
    item,
    onClose,
    country,
    onBuyClick,
    historyBySeller: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? {} : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const historyBySeller = t2;
  let t3;
  let t4;
  if ($[2] !== onClose) {
    t3 = () => {
      const handler = e => {
        if (e.key === "Escape") {
          onClose();
        }
      };
      document.addEventListener("keydown", handler);
      return () => document.removeEventListener("keydown", handler);
    };
    t4 = [onClose];
    $[2] = onClose;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  useEffect(_temp4, t5);
  const priceOf = _temp5;
  const isAmazon = _temp6;
  let t10;
  let t11;
  let t12;
  let t13;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[6] !== country || $[7] !== historyBySeller || $[8] !== item.sellers || $[9] !== onBuyClick || $[10] !== onClose) {
    let t14;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = (a, b) => conditionRank(a) - conditionRank(b) || isAmazon(b) - isAmazon(a) || (b.spec_match_count ?? 0) - (a.spec_match_count ?? 0) || priceOf(a) - priceOf(b);
      $[19] = t14;
    } else {
      t14 = $[19];
    }
    const allSellers = [...(item.sellers ?? [])].sort(t14);
    const newSellers = allSellers.filter(_temp7);
    const refurbSellers = allSellers.filter(isRefurbished);
    let t15;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = group => {
        const priced = group.filter(_temp8);
        if (!priced.length) {
          return null;
        }
        return priced.reduce((min, s_3) => priceOf(s_3) < priceOf(min) ? s_3 : min, priced[0]).id;
      };
      $[20] = t15;
    } else {
      t15 = $[20];
    }
    const cheapestIdIn = t15;
    const cheapestNewId = cheapestIdIn(newSellers);
    const cheapestRefurbId = cheapestIdIn(refurbSellers);
    const sellers = allSellers;
    t13 = createPortal;
    t11 = "fixed inset-0 z-50 flex justify-end";
    if ($[21] !== onClose) {
      t12 = <div className="absolute inset-0 bg-foreground/20 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />;
      $[21] = onClose;
      $[22] = t12;
    } else {
      t12 = $[22];
    }
    t8 = "relative z-10 flex h-full w-full max-w-md flex-col bg-card shadow-2xl ring-1 ring-border/60";
    let t16;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = <h2 className="text-base font-bold tracking-tight">Where to buy</h2>;
      $[23] = t16;
    } else {
      t16 = $[23];
    }
    const t17 = <div>{t16}<p className="mt-0.5 text-[11px] text-muted-foreground">{sellers.length} {sellers.length === 1 ? "seller" : "sellers"} found</p></div>;
    let t18;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <svg viewBox="0 0 16 16" fill="currentColor" className="size-4"><path d="M3.72 3.72a.75.75 0 0 1 1.06 0L8 6.94l3.22-3.22a.75.75 0 1 1 1.06 1.06L9.06 8l3.22 3.22a.75.75 0 1 1-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 0 1-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 0 1 0-1.06Z" /></svg>;
      $[24] = t18;
    } else {
      t18 = $[24];
    }
    let t19;
    if ($[25] !== onClose) {
      t19 = <button onClick={onClose} aria-label="Close" className="grid size-8 shrink-0 place-items-center rounded-full bg-surface text-muted-foreground transition-colors hover:bg-foreground hover:text-background">{t18}</button>;
      $[25] = onClose;
      $[26] = t19;
    } else {
      t19 = $[26];
    }
    if ($[27] !== t17 || $[28] !== t19) {
      t9 = <div className="flex shrink-0 items-start justify-between border-b border-border/60 px-5 py-4">{t17}{t19}</div>;
      $[27] = t17;
      $[28] = t19;
      $[29] = t9;
    } else {
      t9 = $[29];
    }
    t10 = sellers.length > 0 && <div className="shrink-0 border-b border-border/60 bg-surface/60 px-5 py-3"><p className="text-[11px] leading-relaxed text-muted-foreground">These are all configurations of this laptop sold by different retailers, so the specs can vary between listings. Check the specs carefully to find the exact model shown on this page — a{" "}<span className="inline-flex items-center align-middle text-brand"><IconCheck /></span>{" "}marks a spec that matches this configuration.</p></div>;
    t6 = "flex-1 overflow-y-auto p-4";
    t7 = sellers.length > 0 ? <div className="space-y-3">{newSellers.map(seller => <SellerCard key={seller.id} seller={seller} cheapest={seller.id === cheapestNewId} country={country} onBuyClick={onBuyClick} history={historyBySeller[seller.id]} />)}{refurbSellers.length > 0 && <><div className="flex items-center gap-3 pt-2"><span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Refurbished</span><span className="h-px flex-1 bg-border/60" /></div><p className="text-[11px] leading-relaxed text-muted-foreground">Used machines, tested and graded for condition by the seller. Cheaper than new, but check the grade and the seller's warranty before buying.</p>{refurbSellers.map(seller_0 => <SellerCard key={seller_0.id} seller={seller_0} cheapest={seller_0.id === cheapestRefurbId} country={country} onBuyClick={onBuyClick} history={historyBySeller[seller_0.id]} />)}</>}</div> : <div className="flex h-full flex-col items-center justify-center gap-2 text-center"><p className="text-sm font-medium text-foreground">No sellers listed</p><p className="text-xs text-muted-foreground">Try searching directly on Amazon or the manufacturer's site.</p></div>;
    $[6] = country;
    $[7] = historyBySeller;
    $[8] = item.sellers;
    $[9] = onBuyClick;
    $[10] = onClose;
    $[11] = t10;
    $[12] = t11;
    $[13] = t12;
    $[14] = t13;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t10 = $[11];
    t11 = $[12];
    t12 = $[13];
    t13 = $[14];
    t6 = $[15];
    t7 = $[16];
    t8 = $[17];
    t9 = $[18];
  }
  let t14;
  if ($[30] !== t6 || $[31] !== t7) {
    t14 = <div className={t6}>{t7}</div>;
    $[30] = t6;
    $[31] = t7;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t10 || $[34] !== t14 || $[35] !== t8 || $[36] !== t9) {
    t15 = <div className={t8}>{t9}{t10}{t14}</div>;
    $[33] = t10;
    $[34] = t14;
    $[35] = t8;
    $[36] = t9;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t11 || $[39] !== t12 || $[40] !== t15) {
    t16 = <div className={t11}>{t12}{t15}</div>;
    $[38] = t11;
    $[39] = t12;
    $[40] = t15;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  let t17;
  if ($[42] !== t13 || $[43] !== t16) {
    t17 = t13(t16, document.body);
    $[42] = t13;
    $[43] = t16;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  return t17;
}
function _temp8(s_2) {
  return s_2.item_price != null;
}
function _temp7(s_1) {
  return !isRefurbished(s_1);
}
function _temp6(s_0) {
  return s_0.seller_name === "Amazon" ? 1 : 0;
}
function _temp5(s) {
  return s.item_price ?? Infinity;
}
function _temp4() {
  document.body.style.overflow = "hidden";
  return _temp3;
}
function _temp3() {
  document.body.style.overflow = "";
}
function CitationTooltip(t0) {
  const $ = _c(18);
  const {
    citation,
    num
  } = t0;
  let t1;
  if ($[0] !== num) {
    t1 = <sup className="cursor-default select-none text-[12px] font-bold text-brand/60 transition-colors group-hover/cite:text-brand">[{num}]</sup>;
    $[0] = num;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== citation.article_source) {
    t2 = <p className="text-xs font-semibold text-foreground">{citation.article_source}</p>;
    $[2] = citation.article_source;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== citation.title) {
    t3 = citation.title && <p className="mt-1 line-clamp-2 text-[11px] leading-snug text-muted-foreground">{citation.title}</p>;
    $[4] = citation.title;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== citation.author) {
    t4 = citation.author && <p className="mt-1.5 text-[11px] text-muted-foreground/60">{citation.author}</p>;
    $[6] = citation.author;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== citation.url) {
    t5 = citation.url && <a href={citation.url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline">View source ↗</a>;
    $[8] = citation.url;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t2 || $[11] !== t3 || $[12] !== t4 || $[13] !== t5) {
    t6 = <span className="pointer-events-none absolute bottom-full left-1/2 z-20 w-72 -translate-x-1/2 pb-2 opacity-0 transition-opacity duration-150 group-hover/cite:pointer-events-auto group-hover/cite:opacity-100"><span className="block rounded-2xl bg-card p-4 text-left shadow-xl ring-1 ring-border/80">{t2}{t3}{t4}{t5}</span></span>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t1 || $[16] !== t6) {
    t7 = <span className="group/cite relative inline">{t1}{t6}</span>;
    $[15] = t1;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
}
function renderWithCitations(text: string, citations: WikiCitation[]): ReactNode {
  const citMap = new Map(citations.map(c => [c.source_id, c]));
  let n = 0;
  return text.split(/(\[cite:[^\]]+\])/).map((part, i) => {
    const m = part.match(/^\[cite:([^\]]+)\]$/);
    if (m) {
      const cit = citMap.get(m[1]);
      if (!cit) return null;
      return <CitationTooltip key={i} citation={cit} num={++n} />;
    }
    return part || null;
  });
}
function renderDeepReview(rawText: string, citations: WikiCitation[]): ReactNode {
  const citMap = new Map(citations.map(c => [c.source_id, c]));
  let n = 0;
  const elements: ReactNode[] = [];
  rawText.split("\n").forEach((line, i) => {
    const trimmed = line.trim();
    if (!trimmed) return;
    const hMatch = trimmed.match(/^(#{1,3})\s+(.+)$/);
    if (hMatch) {
      const level = hMatch[1].length;
      const headingText = hMatch[2].replace(/\[cite:[^\]]+\]/g, "").trim();
      // This block renders below the page <h1> and the "About this laptop" <h2>,
      // so the deepest markdown level starts at <h3> to keep heading order from
      // skipping levels (# → h3, ## → h4, ### → h5).
      if (level === 1) {
        elements.push(<h3 key={i} className="mt-8 text-xl font-bold tracking-tight text-foreground">
            {headingText}
          </h3>);
      } else if (level === 2) {
        elements.push(<h4 key={i} className="mt-6 text-base font-bold tracking-tight text-foreground">
            {headingText}
          </h4>);
      } else {
        elements.push(<h5 key={i} className="mt-4 text-sm font-semibold text-foreground">
            {headingText}
          </h5>);
      }
    } else {
      const parts = trimmed.split(/(\[cite:[^\]]+\])/).map((part, j) => {
        const m = part.match(/^\[cite:([^\]]+)\]$/);
        if (m) {
          const cit = citMap.get(m[1]);
          if (!cit) return null;
          return <CitationTooltip key={j} citation={cit} num={++n} />;
        }
        return part || null;
      });
      elements.push(<p key={i} className="mt-3 text-sm leading-relaxed text-muted-foreground">
          {parts}
        </p>);
    }
  });
  return <div>{elements}</div>;
}
const PRESET_KEYWORDS = ["good", "great", "love", "excellent", "perfect", "bad", "poor", "disappointing", "broken", "school", "work", "gaming", "travel", "battery", "fast", "slow", "keyboard", "screen"];
function highlightKeywords(text: string, keywords: string[]): ReactNode {
  if (!keywords.length) return text;
  const escaped = keywords.map(k => k.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const pattern = new RegExp(`(${escaped.join("|")})`, "gi");
  const parts = text.split(pattern);
  return <>
      {parts.map((part, i) => keywords.some(kw => kw.toLowerCase() === part.toLowerCase()) ? <mark key={i} className="rounded-sm bg-brand/20 px-0.5 not-italic text-foreground">
            {part}
          </mark> : part || null)}
    </>;
}
type SortOrder = "recent" | "oldest" | "highest" | "lowest";
const SORT_LABELS: {
  value: SortOrder;
  label: string;
}[] = [{
  value: "recent",
  label: "Most recent"
}, {
  value: "oldest",
  label: "Oldest"
}, {
  value: "highest",
  label: "Highest rated"
}, {
  value: "lowest",
  label: "Lowest rated"
}];
function ReviewsSection(t0) {
  const $ = _c(76);
  const {
    reviews
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [activeKeywords, setActiveKeywords] = useState(t1);
  const [customInput, setCustomInput] = useState("");
  const [starFilter, setStarFilter] = useState(null);
  const [sortOrder, setSortOrder] = useState("recent");
  const total = reviews.length;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [5, 4, 3, 2, 1];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== reviews || $[3] !== total) {
    t3 = t2.map(star => {
      const count = reviews.filter(r => r.rating === star).length;
      return {
        star,
        count,
        pct: total ? Math.round(count / total * 100) : 0
      };
    });
    $[2] = reviews;
    $[3] = total;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const distribution = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = kw => {
      const lower = kw.toLowerCase().trim();
      setActiveKeywords(prev => prev.includes(lower) ? prev.filter(k => k !== lower) : [...prev, lower]);
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const toggleKeyword = t4;
  let t5;
  if ($[6] !== activeKeywords || $[7] !== customInput) {
    t5 = () => {
      const lower_0 = customInput.toLowerCase().trim();
      if (lower_0 && !activeKeywords.includes(lower_0)) {
        setActiveKeywords(prev_0 => [...prev_0, lower_0]);
      }
      setCustomInput("");
    };
    $[6] = activeKeywords;
    $[7] = customInput;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const addCustom = t5;
  const hasFilters = activeKeywords.length > 0 || starFilter !== null;
  let t10;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[9] !== activeKeywords || $[10] !== addCustom || $[11] !== customInput || $[12] !== distribution || $[13] !== hasFilters || $[14] !== reviews || $[15] !== sortOrder || $[16] !== starFilter || $[17] !== total) {
    let t11;
    if ($[23] !== sortOrder) {
      t11 = (a, b) => {
        if (sortOrder === "highest") {
          return b.rating - a.rating;
        }
        if (sortOrder === "lowest") {
          return a.rating - b.rating;
        }
        const da = a.review_posted_date ? new Date(a.review_posted_date).getTime() : 0;
        const db = b.review_posted_date ? new Date(b.review_posted_date).getTime() : 0;
        return sortOrder === "recent" ? db - da : da - db;
      };
      $[23] = sortOrder;
      $[24] = t11;
    } else {
      t11 = $[24];
    }
    const filtered = [...reviews].filter(r_0 => starFilter === null || r_0.rating === starFilter).filter(r_1 => activeKeywords.length === 0 || activeKeywords.some(kw_0 => r_1.review_text.toLowerCase().includes(kw_0) || r_1.title.toLowerCase().includes(kw_0))).sort(t11);
    if ($[25] !== distribution || $[26] !== starFilter) {
      t8 = <div className="space-y-1.5">{distribution.map(t12 => {
          const {
            star: star_0,
            count: count_0,
            pct
          } = t12;
          const isSelected = starFilter === star_0;
          const isDimmed = starFilter !== null && !isSelected;
          return <button key={star_0} onClick={() => setStarFilter(isSelected ? null : star_0)} title={isSelected ? "Click to deselect" : undefined} className={`group flex w-full cursor-pointer items-center gap-3 rounded-xl px-2 py-1 transition-all ${isSelected ? "ring-1 ring-brand/30 bg-brand/5 hover:ring-border/60 hover:bg-foreground/4" : "hover:bg-foreground/4"} ${isDimmed ? "opacity-40" : ""}`}><span className="w-8 shrink-0 text-right text-xs font-medium text-muted-foreground">{star_0}★</span><div className="h-2 flex-1 overflow-hidden rounded-full bg-foreground/8"><div className={`h-full rounded-full transition-all duration-500 ${isSelected ? "bg-brand group-hover:bg-foreground/30" : "bg-amber-400"}`} style={{
                width: pct > 0 ? `${pct}%` : "2px"
              }} /></div><span className="w-8 shrink-0 text-right text-xs font-bold tabular-nums">{pct}%</span><span className="w-10 shrink-0 text-[11px] tabular-nums text-muted-foreground">({count_0})</span><span className={`w-4 shrink-0 text-center text-[11px] font-bold text-muted-foreground transition-opacity ${isSelected ? "opacity-0 group-hover:opacity-100" : "opacity-0"}`}>×</span></button>;
        })}</div>;
      $[25] = distribution;
      $[26] = starFilter;
      $[27] = t8;
    } else {
      t8 = $[27];
    }
    let t12;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Sort by</p>;
      $[28] = t12;
    } else {
      t12 = $[28];
    }
    let t13;
    if ($[29] !== sortOrder) {
      t13 = SORT_LABELS.map(t14 => {
        const {
          value,
          label
        } = t14;
        return <button key={value} onClick={() => setSortOrder(value)} className={`rounded-full px-3 py-1 text-[11px] font-semibold transition-all ${sortOrder === value ? "bg-brand text-brand-foreground" : "border border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground"}`}>{label}</button>;
      });
      $[29] = sortOrder;
      $[30] = t13;
    } else {
      t13 = $[30];
    }
    let t14;
    if ($[31] !== t13) {
      t14 = <div className="px-4 py-3">{t12}<div className="flex flex-wrap gap-2">{t13}</div></div>;
      $[31] = t13;
      $[32] = t14;
    } else {
      t14 = $[32];
    }
    let t15;
    if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Filter by keyword</p>;
      $[33] = t15;
    } else {
      t15 = $[33];
    }
    let t16;
    if ($[34] !== activeKeywords) {
      t16 = PRESET_KEYWORDS.map(kw_1 => {
        const active = activeKeywords.includes(kw_1.toLowerCase());
        return <button key={kw_1} onClick={() => {
          if (!active) {
            toggleKeyword(kw_1);
          }
        }} disabled={active} className={`rounded-full px-3 py-1 text-[11px] font-semibold transition-all ${active ? "border border-border text-muted-foreground opacity-35 cursor-default" : "border border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground"}`}>{kw_1}</button>;
      });
      $[34] = activeKeywords;
      $[35] = t16;
    } else {
      t16 = $[35];
    }
    let t17;
    if ($[36] !== t16) {
      t17 = <div className="flex flex-wrap gap-2">{t16}</div>;
      $[36] = t16;
      $[37] = t17;
    } else {
      t17 = $[37];
    }
    let t18;
    if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = e => setCustomInput(e.target.value);
      $[38] = t18;
    } else {
      t18 = $[38];
    }
    let t19;
    if ($[39] !== addCustom) {
      t19 = e_0 => {
        if (e_0.key === "Enter") {
          e_0.preventDefault();
          addCustom();
        }
      };
      $[39] = addCustom;
      $[40] = t19;
    } else {
      t19 = $[40];
    }
    let t20;
    if ($[41] !== customInput || $[42] !== t19) {
      t20 = <input type="text" value={customInput} onChange={t18} onKeyDown={t19} placeholder={"Add a custom keyword\u2026"} className="h-9 flex-1 rounded-xl border border-border bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-brand" />;
      $[41] = customInput;
      $[42] = t19;
      $[43] = t20;
    } else {
      t20 = $[43];
    }
    let t21;
    if ($[44] !== customInput) {
      t21 = customInput.trim();
      $[44] = customInput;
      $[45] = t21;
    } else {
      t21 = $[45];
    }
    const t22 = !t21;
    let t23;
    if ($[46] !== addCustom || $[47] !== t22) {
      t23 = <button onClick={addCustom} disabled={t22} className="h-9 rounded-xl bg-brand px-4 text-sm font-semibold text-brand-foreground transition-opacity disabled:opacity-40">Add</button>;
      $[46] = addCustom;
      $[47] = t22;
      $[48] = t23;
    } else {
      t23 = $[48];
    }
    let t24;
    if ($[49] !== t20 || $[50] !== t23) {
      t24 = <div className="mt-2.5 flex gap-2">{t20}{t23}</div>;
      $[49] = t20;
      $[50] = t23;
      $[51] = t24;
    } else {
      t24 = $[51];
    }
    let t25;
    if ($[52] !== activeKeywords) {
      t25 = activeKeywords.length > 0 && <div className="mt-2 flex flex-wrap gap-2">{activeKeywords.map(kw_2 => <button key={kw_2} onClick={() => toggleKeyword(kw_2)} className="inline-flex items-center gap-1.5 rounded-full bg-brand px-3 py-1 text-[11px] font-semibold text-brand-foreground">{kw_2}<span aria-hidden="true" className="text-[10px] opacity-70">×</span></button>)}</div>;
      $[52] = activeKeywords;
      $[53] = t25;
    } else {
      t25 = $[53];
    }
    let t26;
    if ($[54] !== t17 || $[55] !== t24 || $[56] !== t25) {
      t26 = <div className="border-t border-border/60 px-4 py-3">{t15}{t17}{t24}{t25}</div>;
      $[54] = t17;
      $[55] = t24;
      $[56] = t25;
      $[57] = t26;
    } else {
      t26 = $[57];
    }
    if ($[58] !== t14 || $[59] !== t26) {
      t9 = <div className="mt-5 overflow-hidden rounded-2xl bg-surface/60 ring-1 ring-border/60">{t14}{t26}</div>;
      $[58] = t14;
      $[59] = t26;
      $[60] = t9;
    } else {
      t9 = $[60];
    }
    const t27 = hasFilters ? <><span className="font-semibold text-foreground">{filtered.length}</span> of {total} reviews{starFilter !== null && <> · <span className="font-semibold text-foreground">{"\u2605".repeat(starFilter)} only</span></>}{activeKeywords.length > 0 && <> · matching {activeKeywords.map(_temp9)}</>}</> : <>All <span className="font-semibold text-foreground">{total}</span> reviews</>;
    let t28;
    if ($[61] !== t27) {
      t28 = <p className="text-[11px] text-muted-foreground">{t27}</p>;
      $[61] = t27;
      $[62] = t28;
    } else {
      t28 = $[62];
    }
    let t29;
    if ($[63] !== hasFilters) {
      t29 = hasFilters && <button onClick={() => {
        setActiveKeywords([]);
        setStarFilter(null);
      }} className="shrink-0 text-[11px] font-medium text-muted-foreground hover:text-foreground">Clear filters</button>;
      $[63] = hasFilters;
      $[64] = t29;
    } else {
      t29 = $[64];
    }
    if ($[65] !== t28 || $[66] !== t29) {
      t10 = <div className="mt-4 flex items-center justify-between gap-3">{t28}{t29}</div>;
      $[65] = t28;
      $[66] = t29;
      $[67] = t10;
    } else {
      t10 = $[67];
    }
    t6 = "mt-3 space-y-3";
    t7 = filtered.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">No reviews match those filters.</p> : filtered.map(r_2 => <div key={r_2.id} className="rounded-2xl bg-surface p-4 ring-1 ring-border/50"><div className="flex items-start justify-between gap-3"><div><p className="text-sm font-semibold">{r_2.reviewer_name}</p>{r_2.review_country && <p className="mt-0.5 text-[11px] text-muted-foreground">{r_2.review_country}</p>}</div><div className="shrink-0 text-right"><span className="text-xs text-amber-400">{"\u2605".repeat(Math.max(0, r_2.rating))}<span className="text-border">{"\u2605".repeat(Math.max(0, 5 - r_2.rating))}</span></span>{r_2.review_posted_date && <p className="mt-0.5 text-[10px] text-muted-foreground">{new Date(r_2.review_posted_date).toLocaleDateString("en-GB", {
              year: "numeric",
              month: "short"
            })}</p>}</div></div>{r_2.title && <p className="mt-2 text-xs font-semibold">{highlightKeywords(r_2.title, activeKeywords)}</p>}<p className="mt-1 text-xs leading-relaxed text-muted-foreground">{highlightKeywords(r_2.review_text, activeKeywords)}</p>{r_2.helpful_count > 0 && <p className="mt-2 text-[10px] text-muted-foreground/60">{r_2.helpful_count} {r_2.helpful_count === 1 ? "person" : "people"} found this helpful</p>}</div>);
    $[9] = activeKeywords;
    $[10] = addCustom;
    $[11] = customInput;
    $[12] = distribution;
    $[13] = hasFilters;
    $[14] = reviews;
    $[15] = sortOrder;
    $[16] = starFilter;
    $[17] = total;
    $[18] = t10;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t10 = $[18];
    t6 = $[19];
    t7 = $[20];
    t8 = $[21];
    t9 = $[22];
  }
  let t11;
  if ($[68] !== t6 || $[69] !== t7) {
    t11 = <div className={t6}>{t7}</div>;
    $[68] = t6;
    $[69] = t7;
    $[70] = t11;
  } else {
    t11 = $[70];
  }
  let t12;
  if ($[71] !== t10 || $[72] !== t11 || $[73] !== t8 || $[74] !== t9) {
    t12 = <div>{t8}{t9}{t10}{t11}</div>;
    $[71] = t10;
    $[72] = t11;
    $[73] = t8;
    $[74] = t9;
    $[75] = t12;
  } else {
    t12 = $[75];
  }
  return t12;
}
function _temp9(kw_3, i) {
  return <span key={kw_3}>{i > 0 && ", "}<span className="font-semibold text-foreground">"{kw_3}"</span></span>;
}
function AccordionSection(t0) {
  const $ = _c(10);
  const {
    value,
    title,
    children,
    highlight
  } = t0;
  const t1 = `overflow-hidden rounded-2xl px-5 ${highlight ? "border border-brand/30 bg-brand/5 ring-1 ring-brand/20" : "border border-border/60 bg-card"}`;
  const t2 = `py-4 tracking-tight hover:no-underline ${highlight ? "text-base font-bold text-foreground" : "text-sm font-semibold"}`;
  let t3;
  if ($[0] !== t2 || $[1] !== title) {
    t3 = <AccordionTrigger className={t2}>{title}</AccordionTrigger>;
    $[0] = t2;
    $[1] = title;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== children) {
    t4 = <AccordionContent className="pb-5 pt-1">{children}</AccordionContent>;
    $[3] = children;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t1 || $[6] !== t3 || $[7] !== t4 || $[8] !== value) {
    t5 = <AccordionItem value={value} className={t1}>{t3}{t4}</AccordionItem>;
    $[5] = t1;
    $[6] = t3;
    $[7] = t4;
    $[8] = value;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
export function LaptopInformationDetail(t0) {
  const $ = _c(144);
  const {
    item,
    matchScores,
    reference: t1,
    answers: t2,
    allItems: t3,
    country,
    series,
    config
  } = t0;
  let t4;
  if ($[0] !== t1) {
    t4 = t1 === undefined ? {} : t1;
    $[0] = t1;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const reference = t4;
  const answers = t2 === undefined ? {} : t2;
  let t5;
  if ($[2] !== t3) {
    t5 = t3 === undefined ? [] : t3;
    $[2] = t3;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const allItems = t5;
  const [imgIdx, setImgIdx] = useState(0);
  const [showSellers, setShowSellers] = useState(false);
  const [showScoreModal, setShowScoreModal] = useState(false);
  const [mounted, setMounted] = useState(false);
  let t6;
  let t7;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setMounted(true);
    };
    t7 = [];
    $[4] = t6;
    $[5] = t7;
  } else {
    t6 = $[4];
    t7 = $[5];
  }
  useEffect(t6, t7);
  const {
    handleBuyClick,
    gate: buyerSurveyGate
  } = useBuyerSurveyGate({
    laptopName: item.laptop.name,
    laptopSlug: toSlug(item.laptop.series),
    country: country ?? answers.country as string | undefined ?? "gb"
  });
  const countryCode = country ?? answers.country as string | undefined ?? "gb";
  let t8;
  if ($[6] !== item.laptop.series || $[7] !== series) {
    t8 = series ?? toSlug(item.laptop.series);
    $[6] = item.laptop.series;
    $[7] = series;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const seriesSlug = t8;
  const configNumber = config ?? String(item.laptop.config_number);
  let t9;
  if ($[9] !== item.amazon_product_desc) {
    t9 = Array.from(new Set((item.amazon_product_desc ?? []).map(_temp10).filter(_temp11))).slice(0, 2);
    $[9] = item.amazon_product_desc;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const amazonTitles = t9;
  const imgs = item.laptop.img_urls;
  const hasSellers = (item.sellers?.length ?? 0) > 0;
  const amazonTarget = getAmazonTarget(item, countryCode);
  const amazonUrl = amazonTarget?.url ?? null;
  const amazonPrice = amazonTarget && amazonTarget.price != null ? {
    price: amazonTarget.price,
    sym: amazonTarget.sym
  } : null;
  const cheapestRefurbished = (item.sellers ?? []).filter(_temp12).map(_temp13).sort(_temp14)[0] ?? null;
  const {
    history: priceHistory,
    loading: priceHistoryLoading
  } = usePriceHistory(countryCode, seriesSlug, configNumber, mounted);
  let map;
  if ($[11] !== priceHistory?.sellers) {
    map = {};
    for (const s_1 of priceHistory?.sellers ?? []) {
      map[s_1.id] = s_1.points;
    }
    $[11] = priceHistory?.sellers;
    $[12] = map;
  } else {
    map = $[12];
  }
  const historyBySeller = map;
  const summaryRaw = item.wiki?.page?.short_summary ?? null;
  const deepReviewRaw = item.wiki?.page?.deep_review ?? null;
  let t10;
  if ($[13] !== item.wiki?.page?.citations?.by_section?.short_summary) {
    t10 = item.wiki?.page?.citations?.by_section?.short_summary ?? [];
    $[13] = item.wiki?.page?.citations?.by_section?.short_summary;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const summaryCitations = t10;
  let t11;
  if ($[15] !== item.wiki?.page?.citations?.by_section?.deep_review) {
    t11 = item.wiki?.page?.citations?.by_section?.deep_review ?? [];
    $[15] = item.wiki?.page?.citations?.by_section?.deep_review;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const deepReviewCitations = t11;
  const pros = (item.wiki?.page?.pros ?? []).map(stripCitations);
  const cons = (item.wiki?.page?.cons ?? []).map(stripCitations);
  let t12;
  if ($[17] !== item.cpu.name) {
    t12 = {
      label: "Processor",
      value: item.cpu.name
    };
    $[17] = item.cpu.name;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== item.gpu.name) {
    t13 = {
      label: "GPU",
      value: item.gpu.name
    };
    $[19] = item.gpu.name;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const t14 = `${item.specs.ram.size_gb} GB${item.specs.ram.generation ? ` ${item.specs.ram.generation}` : ""}`;
  let t15;
  if ($[21] !== t14) {
    t15 = {
      label: "RAM",
      value: t14
    };
    $[21] = t14;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  const t16 = `${item.specs.storage.size_gb >= 1000 ? `${item.specs.storage.size_gb / 1000} TB` : `${item.specs.storage.size_gb} GB`} ${item.specs.storage.type}`;
  let t17;
  if ($[23] !== t16) {
    t17 = {
      label: "Storage",
      value: t16
    };
    $[23] = t16;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  const t18 = `${item.specs.display.inches}" ${item.specs.display.resolution}${item.specs.display.panel_type ? ` ${item.specs.display.panel_type}` : ""}`;
  let t19;
  if ($[25] !== t18) {
    t19 = {
      label: "Display",
      value: t18
    };
    $[25] = t18;
    $[26] = t19;
  } else {
    t19 = $[26];
  }
  let t20;
  if ($[27] !== item.specs.weight_kg) {
    t20 = item.specs.weight_kg != null ? [{
      label: "Weight",
      value: `${item.specs.weight_kg} kg`
    }] : [];
    $[27] = item.specs.weight_kg;
    $[28] = t20;
  } else {
    t20 = $[28];
  }
  let t21;
  if ($[29] !== item.specs.operating_system) {
    t21 = item.specs.operating_system ? [{
      label: "Operating System",
      value: item.specs.operating_system
    }] : [];
    $[29] = item.specs.operating_system;
    $[30] = t21;
  } else {
    t21 = $[30];
  }
  let t22;
  if ($[31] !== item.specs.body_material) {
    t22 = item.specs.body_material ? [{
      label: "Body Material",
      value: item.specs.body_material
    }] : [];
    $[31] = item.specs.body_material;
    $[32] = t22;
  } else {
    t22 = $[32];
  }
  let t23;
  if ($[33] !== t12 || $[34] !== t13 || $[35] !== t15 || $[36] !== t17 || $[37] !== t19 || $[38] !== t20 || $[39] !== t21 || $[40] !== t22) {
    t23 = [t12, t13, t15, t17, t19, ...t20, ...t21, ...t22];
    $[33] = t12;
    $[34] = t13;
    $[35] = t15;
    $[36] = t17;
    $[37] = t19;
    $[38] = t20;
    $[39] = t21;
    $[40] = t22;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  const specs = t23;
  const t24 = item.connectivity.num_usb_a > 0 && `${item.connectivity.num_usb_a}× USB-A`;
  const t25 = item.connectivity.num_usb_c > 0 && `${item.connectivity.num_usb_c}× USB-C`;
  const t26 = item.connectivity.has_hdmi && "HDMI";
  const t27 = item.connectivity.has_ethernet && "Ethernet";
  const t28 = item.connectivity.has_audio_jack && "Audio Jack";
  const t29 = item.connectivity.has_card_reader && "SD Card Reader";
  const t30 = item.connectivity.has_mini_display_port && "Mini DisplayPort";
  const t31 = `Wi-Fi ${item.connectivity.wifi_version}`;
  const t32 = `Bluetooth ${item.connectivity.bluetooth_version}`;
  let t33;
  if ($[42] !== t24 || $[43] !== t25 || $[44] !== t26 || $[45] !== t27 || $[46] !== t28 || $[47] !== t29 || $[48] !== t30 || $[49] !== t31 || $[50] !== t32) {
    t33 = [t24, t25, t26, t27, t28, t29, t30, t31, t32].filter(Boolean);
    $[42] = t24;
    $[43] = t25;
    $[44] = t26;
    $[45] = t27;
    $[46] = t28;
    $[47] = t29;
    $[48] = t30;
    $[49] = t31;
    $[50] = t32;
    $[51] = t33;
  } else {
    t33 = $[51];
  }
  const connectivity = t33 as string[];
  const t34 = "px-5 py-10 sm:px-8 sm:py-16";
  const t35 = "mx-auto max-w-7xl";
  const t36 = allItems.length > 0 ? <Link href="/recommendations" className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground">← Back to matches</Link> : <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground"><Link href="/laptops" className="hover:text-foreground">All laptops</Link><span className="mx-1.5">/</span><Link href={`/spec/${countryCode}/${seriesSlug}`} className="hover:text-foreground">{item.laptop.series}</Link></nav>;
  let t37;
  if ($[52] !== item.laptop.config_number || $[53] !== item.laptop.series) {
    t37 = <h1 className="text-3xl font-bold leading-snug tracking-tight sm:text-4xl">{item.laptop.series} Model {item.laptop.config_number}</h1>;
    $[52] = item.laptop.config_number;
    $[53] = item.laptop.series;
    $[54] = t37;
  } else {
    t37 = $[54];
  }
  let t38;
  if ($[55] !== item.laptop.company) {
    t38 = capitalize(item.laptop.company);
    $[55] = item.laptop.company;
    $[56] = t38;
  } else {
    t38 = $[56];
  }
  let t39;
  if ($[57] !== t38) {
    t39 = <p className="text-sm text-muted-foreground">{t38}</p>;
    $[57] = t38;
    $[58] = t39;
  } else {
    t39 = $[58];
  }
  let t40;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <a href="#about" className="text-sm font-medium text-brand underline-offset-2 hover:underline">About this laptop ↓</a>;
    $[59] = t40;
  } else {
    t40 = $[59];
  }
  let t41;
  if ($[60] !== t39) {
    t41 = <div className="mt-1.5 flex items-center gap-3">{t39}{t40}</div>;
    $[60] = t39;
    $[61] = t41;
  } else {
    t41 = $[61];
  }
  let t42;
  if ($[62] !== amazonTitles) {
    t42 = amazonTitles.length > 0 && <p className="mt-2 text-xs leading-relaxed text-muted-foreground">Also sold as:{" "}{amazonTitles.map(_temp15)}</p>;
    $[62] = amazonTitles;
    $[63] = t42;
  } else {
    t42 = $[63];
  }
  let t43;
  if ($[64] !== t37 || $[65] !== t41 || $[66] !== t42) {
    t43 = <div className="mt-6 border-b border-border/60 pb-5">{t37}{t41}{t42}</div>;
    $[64] = t37;
    $[65] = t41;
    $[66] = t42;
    $[67] = t43;
  } else {
    t43 = $[67];
  }
  let t44;
  if ($[68] !== imgIdx || $[69] !== imgs || $[70] !== item.laptop.name) {
    t44 = <div className="overflow-hidden rounded-3xl bg-surface ring-1 ring-border/60">{imgs.length > 0 ? <img src={imgs[imgIdx]} alt={item.laptop.name} width={1024} height={768} className="aspect-[4/3] w-full object-contain transition-opacity duration-300" /> : <div className="aspect-[4/3] w-full bg-surface" />}</div>;
    $[68] = imgIdx;
    $[69] = imgs;
    $[70] = item.laptop.name;
    $[71] = t44;
  } else {
    t44 = $[71];
  }
  let t45;
  if ($[72] !== imgIdx || $[73] !== imgs) {
    t45 = imgs.length > 1 && <div className="mt-3 flex gap-3 overflow-x-auto pb-1">{imgs.map((src, i_0) => <button key={i_0} onClick={() => setImgIdx(i_0)} aria-label={`Show image ${i_0 + 1} of ${imgs.length}`} aria-pressed={i_0 === imgIdx} className={`shrink-0 overflow-hidden rounded-xl ring-1 transition-all ${i_0 === imgIdx ? "ring-2 ring-brand" : "ring-border/60 hover:ring-foreground/40"}`}><img src={src} alt="" width={200} height={150} className="aspect-[4/3] w-24 object-contain" /></button>)}</div>;
    $[72] = imgIdx;
    $[73] = imgs;
    $[74] = t45;
  } else {
    t45 = $[74];
  }
  let t46;
  if ($[75] !== t44 || $[76] !== t45) {
    t46 = <div className="lg:sticky lg:top-20 lg:h-fit">{t44}{t45}</div>;
    $[75] = t44;
    $[76] = t45;
    $[77] = t46;
  } else {
    t46 = $[77];
  }
  let t47;
  if ($[78] !== matchScores || $[79] !== setShowScoreModal) {
    t47 = matchScores && <div className="mt-5 rounded-2xl bg-surface/60 p-5 ring-1 ring-border/60"><div className="flex items-center gap-3"><div className="group relative shrink-0"><button onClick={() => setShowScoreModal(true)} className="flex h-14 cursor-pointer items-center gap-2.5 rounded-2xl px-4 text-white transition-opacity hover:opacity-85" style={{
            backgroundColor: scoreColor(matchScores.overall)
          }} aria-label="See match score breakdown"><span className="text-2xl font-bold leading-none tabular-nums">{matchScores.overall}</span><div className="h-5 w-px bg-white/30" /><span className="text-lg font-bold leading-none text-white/80">?</span></button><div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 w-32 -translate-x-1/2 rounded-lg bg-foreground px-2 py-1.5 text-center text-[10px] leading-snug text-background opacity-0 shadow-lg transition-opacity group-hover:opacity-100">click to see more<div className="absolute left-1/2 top-full -translate-x-1/2 border-[4px] border-transparent border-t-foreground" /></div></div><div><p className="text-sm font-bold leading-none text-foreground">Match</p><p className="mt-1 text-[9px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{scoreBand(matchScores.overall)}</p></div></div><p className="mt-2 text-xs font-medium leading-snug text-muted-foreground">How well this laptop matches your specific needs</p><div className="mt-4 space-y-3 border-t border-border/50 pt-4"><div className="flex items-center justify-between gap-2"><span className="flex items-center gap-1.5 text-[11px] font-medium text-foreground"><IconZap />Within budget</span><BoolBadge value={matchScores.within_budget} /></div><div className="flex items-center justify-between gap-2"><span className="flex items-center gap-1.5 text-[11px] font-medium text-foreground"><IconDisplay />Preferred size</span><BoolBadge value={matchScores.form_factor} /></div>{matchScores.performance !== null && <div><div className="flex items-center justify-between gap-1"><span className="flex items-center gap-1.5 text-[11px] font-medium text-foreground"><IconCpu />Performance</span><span className="text-sm font-bold tabular-nums" style={{
              color: scoreColor(matchScores.performance)
            }}>{matchScores.performance}%</span></div><div className="mt-1.5 h-1 overflow-hidden rounded-full bg-border/60"><div className="h-full rounded-full transition-all duration-500" style={{
              width: barWidth(matchScores.performance),
              backgroundColor: scoreColor(matchScores.performance)
            }} /></div></div>}{matchScores.battery_life !== null && <div><div className="flex items-center justify-between gap-1"><span className="flex items-center gap-1.5 text-[11px] font-medium text-foreground"><IconLeaf />{"Battery & efficiency"}</span><span className="text-sm font-bold tabular-nums" style={{
              color: scoreColor(matchScores.battery_life)
            }}>{matchScores.battery_life}%</span></div><div className="mt-1.5 h-1 overflow-hidden rounded-full bg-border/60"><div className="h-full rounded-full transition-all duration-500" style={{
              width: barWidth(matchScores.battery_life),
              backgroundColor: scoreColor(matchScores.battery_life)
            }} /></div></div>}</div></div>;
    $[78] = matchScores;
    $[79] = setShowScoreModal;
    $[80] = t47;
  } else {
    t47 = $[80];
  }
  const t48 = hasSellers ? <>{amazonPrice && <p className="text-3xl font-bold tracking-tight">{amazonPrice.sym}{amazonPrice.price.toLocaleString("en-GB", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })}</p>}{amazonUrl && <a href={amazonUrl} target="_blank" rel="noreferrer" onClick={e => handleBuyClick(e, {
      url: amazonUrl,
      retailer: "Amazon"
    })} className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-brand text-sm font-semibold text-brand-foreground shadow-sm transition-all hover:opacity-95 active:scale-[0.99]">{amazonPrice ? `Buy on Amazon · ${amazonPrice.sym}${Math.round(amazonPrice.price).toLocaleString("en-GB")}` : "Buy on Amazon"}<svg viewBox="0 0 16 16" fill="currentColor" className="size-4"><path fillRule="evenodd" d="M4.22 11.78a.75.75 0 0 1 0-1.06L8.94 6H5.75a.75.75 0 0 1 0-1.5h5.5a.75.75 0 0 1 .75.75v5.5a.75.75 0 0 1-1.5 0V7.56l-4.72 4.72a.75.75 0 0 1-1.06 0Z" clipRule="evenodd" /></svg></a>}{amazonUrl && <AffiliateDisclosure retailer="Amazon" className="text-center" />}<button onClick={() => setShowSellers(true)} className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl border border-border text-sm font-semibold text-foreground transition-colors hover:border-foreground/40 hover:bg-surface active:scale-[0.99]"><svg viewBox="0 0 16 16" fill="none" className="size-4 shrink-0"><path d="M1.5 2.5h13M1.5 8h13M1.5 13.5h13" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" /></svg>View sellers<span className="rounded-full bg-foreground/8 px-2 py-0.5 text-[11px] font-bold tabular-nums">{item.sellers.length}</span></button><WishlistButton laptopConfigId={item.laptop.id} laptopName={`${item.laptop.series} Model ${item.laptop.config_number}`} country={countryCode} variant="block" />{cheapestRefurbished && <p className="text-center text-[11px] text-muted-foreground">Also available refurbished from{" "}<span className="font-semibold text-foreground">{cheapestRefurbished.sym}{Math.round(cheapestRefurbished.price).toLocaleString("en-GB")}</span></p>}</> : <><div className="flex flex-col items-center justify-center gap-1 rounded-2xl border border-border bg-surface/60 px-4 py-6 text-center"><p className="text-sm font-semibold text-foreground">No sellers</p><p className="text-xs text-muted-foreground">We couldn't find any retailer currently selling this exact laptop.</p></div><WishlistButton laptopConfigId={item.laptop.id} laptopName={`${item.laptop.series} Model ${item.laptop.config_number}`} country={countryCode} variant="block" /></>;
  let t49;
  if ($[81] !== allItems || $[82] !== answers || $[83] !== configNumber || $[84] !== countryCode || $[85] !== item || $[86] !== matchScores || $[87] !== reference || $[88] !== seriesSlug) {
    t49 = matchScores && <ReportActions item={item} scores={matchScores} reference={reference} answers={answers} allItems={allItems} country={countryCode} series={seriesSlug} config={configNumber} />;
    $[81] = allItems;
    $[82] = answers;
    $[83] = configNumber;
    $[84] = countryCode;
    $[85] = item;
    $[86] = matchScores;
    $[87] = reference;
    $[88] = seriesSlug;
    $[89] = t49;
  } else {
    t49 = $[89];
  }
  let t50;
  if ($[90] !== t48 || $[91] !== t49) {
    t50 = <div className="mt-8 space-y-3">{t48}{t49}</div>;
    $[90] = t48;
    $[91] = t49;
    $[92] = t50;
  } else {
    t50 = $[92];
  }
  let t51;
  if ($[93] !== countryCode || $[94] !== item) {
    t51 = displayPrice(item, countryCode)?.price ?? null;
    $[93] = countryCode;
    $[94] = item;
    $[95] = t51;
  } else {
    t51 = $[95];
  }
  let t52;
  if ($[96] !== priceHistory || $[97] !== priceHistoryLoading || $[98] !== t51) {
    t52 = <PriceHistorySection history={priceHistory} loading={priceHistoryLoading} currentPrice={t51} />;
    $[96] = priceHistory;
    $[97] = priceHistoryLoading;
    $[98] = t51;
    $[99] = t52;
  } else {
    t52 = $[99];
  }
  let t53;
  if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = [];
    $[100] = t53;
  } else {
    t53 = $[100];
  }
  let t54;
  if ($[101] !== specs) {
    t54 = <AccordionSection value="specs" title="Technical specifications"><dl className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">{specs.map(_temp16)}</dl></AccordionSection>;
    $[101] = specs;
    $[102] = t54;
  } else {
    t54 = $[102];
  }
  let t55;
  if ($[103] !== connectivity) {
    t55 = connectivity.length > 0 && <AccordionSection value="connectivity" title="Connectivity"><div className="flex flex-wrap gap-2">{connectivity.map(_temp17)}</div></AccordionSection>;
    $[103] = connectivity;
    $[104] = t55;
  } else {
    t55 = $[104];
  }
  let t56;
  if ($[105] !== t54 || $[106] !== t55) {
    t56 = <Accordion type="multiple" defaultValue={t53} className="mt-10 space-y-3">{t54}{t55}</Accordion>;
    $[105] = t54;
    $[106] = t55;
    $[107] = t56;
  } else {
    t56 = $[107];
  }
  let t57;
  if ($[108] !== t47 || $[109] !== t50 || $[110] !== t52 || $[111] !== t56) {
    t57 = <div>{t47}{t50}{t52}{t56}</div>;
    $[108] = t47;
    $[109] = t50;
    $[110] = t52;
    $[111] = t56;
    $[112] = t57;
  } else {
    t57 = $[112];
  }
  let t58;
  if ($[113] !== t46 || $[114] !== t57) {
    t58 = <div className="mt-6 grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:gap-16">{t46}{t57}</div>;
    $[113] = t46;
    $[114] = t57;
    $[115] = t58;
  } else {
    t58 = $[115];
  }
  const t59 = (pros.length > 0 || cons.length > 0 || summaryRaw || deepReviewRaw || item.user_reviews.length > 0) && <div id="about" className="mt-16 border-t border-border/60 pt-10"><h2 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">About this laptop</h2><p className="mt-3 text-sm leading-relaxed text-muted-foreground">The review on Plample is a consensus from multiple reviewers across the web, so you don't have to spend hours hunting down what you need to make your decision. Each claim is backed by cited sources. Hover over any reference number to see where it comes from.</p><Accordion type="multiple" defaultValue={["about-deep"]} className="mt-6 space-y-3">{deepReviewRaw && <AccordionSection value="about-deep" title="What other reviewers think of this laptop" highlight={true}>{renderDeepReview(deepReviewRaw, deepReviewCitations)}</AccordionSection>}{item.user_reviews.length > 0 && <AccordionSection value="about-reviews" title={`Reviews from actual buyers (${item.user_reviews.length})`}><ReviewsSection reviews={item.user_reviews} /></AccordionSection>}{(pros.length > 0 || cons.length > 0) && <AccordionSection value="about-pros" title="Pros and cons summarised"><div className="grid gap-6 sm:grid-cols-2">{pros.length > 0 && <div><p className="text-[11px] font-semibold uppercase tracking-widest text-brand">Pros</p><ul className="mt-3 space-y-2">{pros.map(_temp18)}</ul></div>}{cons.length > 0 && <div><p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">Cons</p><ul className="mt-3 space-y-2">{cons.map(_temp19)}</ul></div>}</div></AccordionSection>}{summaryRaw && <AccordionSection value="about-summary" title="Summary"><p className="text-sm leading-relaxed text-muted-foreground">{renderWithCitations(summaryRaw, summaryCitations)}</p></AccordionSection>}</Accordion></div>;
  let t60;
  if ($[116] !== t36 || $[117] !== t43 || $[118] !== t58 || $[119] !== t59) {
    t60 = <main className={t34}><div className={t35}>{t36}{t43}{t58}{t59}</div></main>;
    $[116] = t36;
    $[117] = t43;
    $[118] = t58;
    $[119] = t59;
    $[120] = t60;
  } else {
    t60 = $[120];
  }
  let t61;
  if ($[121] !== countryCode || $[122] !== handleBuyClick || $[123] !== historyBySeller || $[124] !== item || $[125] !== mounted || $[126] !== setShowSellers || $[127] !== showSellers) {
    t61 = mounted && showSellers && <SellersDrawer item={item} onClose={() => setShowSellers(false)} country={countryCode} onBuyClick={handleBuyClick} historyBySeller={historyBySeller} />;
    $[121] = countryCode;
    $[122] = handleBuyClick;
    $[123] = historyBySeller;
    $[124] = item;
    $[125] = mounted;
    $[126] = setShowSellers;
    $[127] = showSellers;
    $[128] = t61;
  } else {
    t61 = $[128];
  }
  const t62 = mounted && buyerSurveyGate;
  let t63;
  if ($[129] !== allItems || $[130] !== answers || $[131] !== countryCode || $[132] !== item || $[133] !== matchScores || $[134] !== mounted || $[135] !== reference || $[136] !== setShowScoreModal || $[137] !== showScoreModal) {
    t63 = mounted && showScoreModal && matchScores && <ScoreDetailModal item={item} scores={matchScores} rank={item.rank ?? 1} allItems={allItems} reference={reference} answers={answers} country={countryCode} context="laptop" onClose={() => setShowScoreModal(false)} />;
    $[129] = allItems;
    $[130] = answers;
    $[131] = countryCode;
    $[132] = item;
    $[133] = matchScores;
    $[134] = mounted;
    $[135] = reference;
    $[136] = setShowScoreModal;
    $[137] = showScoreModal;
    $[138] = t63;
  } else {
    t63 = $[138];
  }
  let t64;
  if ($[139] !== t60 || $[140] !== t61 || $[141] !== t62 || $[142] !== t63) {
    t64 = <>{t60}{t61}{t62}{t63}</>;
    $[139] = t60;
    $[140] = t61;
    $[141] = t62;
    $[142] = t63;
    $[143] = t64;
  } else {
    t64 = $[143];
  }
  return t64;
}
function _temp19(c_0, i_2) {
  return <li key={i_2} className="text-sm leading-relaxed">{c_0}</li>;
}
function _temp18(p, i_1) {
  return <li key={i_1} className="text-sm leading-relaxed">{p}</li>;
}
function _temp17(c) {
  return <span key={c} className="rounded-full border border-border bg-card px-2.5 py-1 text-[11px] font-medium text-foreground">{c}</span>;
}
function _temp16(s_2) {
  return <div key={s_2.label}><dt className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">{s_2.label}</dt><dd className="mt-1 text-sm font-medium">{s_2.value}</dd></div>;
}
function _temp15(title, i) {
  return <span key={title}>{i > 0 ? " \xB7 " : ""}{title}</span>;
}
function _temp14(a, b) {
  return a.price - b.price;
}
function _temp13(s_0) {
  return {
    price: s_0.item_price as number,
    sym: normCurrencySymbol(s_0.currency)
  };
}
function _temp12(s) {
  return isRefurbished(s) && (s.item_price ?? 0) > 0;
}
function _temp11(t) {
  return Boolean(t);
}
function _temp10(d) {
  return d.title?.trim();
}